import styled from 'styled-components';

const Container = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
    z-index: 1;
  }
  
  /* Scrollbar moderno */
  ::-webkit-scrollbar {
    width: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(248, 249, 252, 0.6);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border-radius: 3px;
    transition: all 0.3s ease;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  }
`;

const Header = styled.div`
  padding: 2rem 1.5rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 0.75rem;
`;

const LogoIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  
  .brand {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
    line-height: 1;
  }
  
  .role {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 0.25rem;
  }
`;

const ProviderInfo = styled.div`
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const ProviderName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const ProviderStatus = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #059669;
    border-radius: 50%;
    margin-right: 0.5rem;
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
`;

const NavTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 1rem 0;
  padding: 0 0.5rem;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: ${props => {
    if (props.active) return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    if (props.disabled) return 'rgba(248, 249, 252, 0.4)';
    return 'transparent';
  }};
  box-shadow: ${props => props.active ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none'};
  border: 1px solid ${props => {
    if (props.active) return 'rgba(59, 130, 246, 0.2)';
    if (props.disabled) return 'rgba(203, 213, 225, 0.4)';
    return 'transparent';
  }};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    ${props => !props.disabled && !props.active && `
      background: rgba(59, 130, 246, 0.04);
      border-color: rgba(59, 130, 246, 0.1);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    `}
  }
`;

const NavIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: ${props => props.active ? '#ffffff' : '#64748b'};
  font-size: 1rem;
  transition: color 0.3s ease;
`;

const NavContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NavLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: 0.125rem;
  color: #1e293b;
  
  ${NavItem}[data-active="true"] & {
    color: #ffffff;
  }
`;

const NavDescription = styled.div`
  font-size: 0.75rem;
  line-height: 1.2;
  font-weight: 400;
  color: #64748b;
  
  ${NavItem}[data-active="true"] & {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 24px;
  background: #ffffff;
  border-radius: 0 2px 2px 0;
  box-shadow: 0 2px 4px rgba(255, 255, 255, 0.3);
`;

const ComingSoon = styled.div`
  font-size: 0.625rem;
  font-weight: 600;
  color: #f59e0b;
  background: rgba(251, 191, 36, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 0.5rem;
`;

const Footer = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
`;

const FooterCard = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 16px;
  padding: 1.5rem;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  margin-bottom: 1rem;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
`;

const FooterContent = styled.div`
  position: relative;
  z-index: 1;
`;

const FooterTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
  line-height: 1.2;
`;

const FooterText = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 1rem 0;
  line-height: 1.4;
`;

const FooterButton = styled.button`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(8px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProviderSidebarStyle = {
  Container,
  Header,
  Logo,
  LogoIcon,
  LogoText,
  ProviderInfo,
  ProviderName,
  ProviderStatus,
  Navigation,
  NavTitle,
  NavItem,
  NavIcon,
  NavContent,
  NavLabel,
  NavDescription,
  ActiveIndicator,
  ComingSoon,
  Footer,
  FooterCard,
  FooterIcon,
  FooterContent,
  FooterTitle,
  FooterText,
  FooterButton
};

export default ProviderSidebarStyle; 